import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  DiscriminatedInspectionEvaluationModel,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const PropsModel = ProjectModel.pick({ inspectionEvaluation: true });
type Props = z.infer<typeof PropsModel>;

export const KsStandardInspectionEvaluationAfter2025: FC<Props> = ({
  inspectionEvaluation,
}) => {
  const { t } = useTranslation();
  const { id } = useId();
  const parsed = DiscriminatedInspectionEvaluationModel.safeParse({
    sheetType: 'KS',
    inspectionEvaluation,
  });

  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }

  const { inspectionAndImprovementByAdministrativeJurisdiction, ...rest } =
    parsed.data.inspectionEvaluation;

  return (
    <>
      <ContentWrapper
        title={t('inspection-and-improvement-by-fund-jurisdiction')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <PinpointMarker
                key="inspection-result"
                pinpoint={{
                  path: { project: id },
                  field: 'ks.standard.inspection-result',
                }}
              >
                <TitledText
                  title={t('inspection-result')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.inspectionResult
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="evaluation"
                pinpoint={{
                  path: { project: id },
                  field: 'ks.standard.evaluation',
                }}
              >
                <TitledText
                  title={t('evaluation')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.evaluation
                  }
                />
              </PinpointMarker>,
            ]}
          />

          <ContentItems
            items={[
              <PinpointMarker
                key="direction-of-improvement"
                pinpoint={{
                  path: { project: id },
                  field: 'ks.standard.direction-of-improvement',
                }}
              >
                <TitledText
                  title={t('direction-of-improvement')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.directionOfImprovement
                  }
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('findings-of-external-experts')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="findings-of-external-experts"
                pinpoint={{
                  path: { project: id },
                  field: 'ks.standard.findings-of-external-experts',
                }}
              >
                <Text
                  variant="body"
                  text={rest.findingsOfExternalExperts}
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('process-leading-to-the-findings-of-review-promotion-team')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="process-leading-to-the-findings-of-review-promotion-team"
                pinpoint={{
                  path: { project: id },
                  field:
                    'ks.standard.process-leading-to-the-findings-of-review-promotion-team',
                }}
              >
                <Text
                  variant="body"
                  text={rest.processLeadingToTheFindingsOfReviewPromotionTeam}
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('improvement-point-based-on-the-findings')}
        borderBottom={0}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="improvement-point-based-on-the-findings"
                pinpoint={{
                  path: { project: id },
                  field: 'ks.standard.improvement-point-based-on-the-findings',
                }}
              >
                <Text
                  variant="body"
                  text={rest.improvementPointBasedOnTheFindings}
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
